import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  makeStyles,
  Slide,
  Typography,
} from '@material-ui/core';
import {Close} from '@material-ui/icons';
import {TransitionProps} from '@mui/material/transitions';
import React, {forwardRef, ReactElement, Ref} from 'react';
import {CardFieldV2} from 'Shared/components/CardFieldV2';
import {SwipeableView} from 'Shared/components/SwipeableView';
import {toKebabCase} from 'Shared/utilities/stringFormatHelpers';
import {BenefitDetail} from '../../models/Policy';

export interface DesktopDialogProps {
  benefitToRender: BenefitDetail;
  open: boolean;
  handleClose: () => void;
}

const useDialogStyles = makeStyles(_theme => ({
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: 0,
    },
  },
  title: {
    borderBottom: '1px solid #ccc',
    backgroundColor: '#F4F8FC',
    color: '#0033A0',
    fontSize: '1rem',
    fontWeight: 700,
  },
  content: {
    padding: '16px 24px',
  },
}));

const RenderAttributes = ({benefitToRender}: {benefitToRender?: BenefitDetail}) => {
  const gridSize = benefitToRender && benefitToRender.attributes.length > 3 ? 4 : 6;
  return (
    <>
      {Array.isArray(benefitToRender?.attributes) &&
        benefitToRender?.attributes.map(({key, value}) => (
          <Grid item md={gridSize} key={toKebabCase(key)}>
            <CardFieldV2 name={key} value={value} />
          </Grid>
        ))}
    </>
  );
};

const DesktopDialog = (props: DesktopDialogProps) => {
  const {benefitToRender, open, handleClose} = props;
  const styles = useDialogStyles();
  const dialogWidth = benefitToRender && benefitToRender.attributes?.length > 3 ? 'lg' : 'md';

  return (
    <Dialog
      fullWidth={true}
      maxWidth={dialogWidth}
      open={open}
      onClose={handleClose}
      aria-labelledby="desktop-dialog-title"
      className={styles.dialog}
    >
      <DialogTitle id="desktop-dialog-title" disableTypography className={styles.title}>
        {benefitToRender?.benefitName}
      </DialogTitle>
      <DialogContent className={styles.content}>
        <Grid container spacing={4}>
          <RenderAttributes benefitToRender={benefitToRender} />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" id="close-dialog-btn">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Transition = forwardRef((props: TransitionProps & {children?: ReactElement}, ref: Ref<unknown>) => (
  <Slide direction="up" ref={ref} {...props} />
));

export interface FullScreenDialogProps {
  open: boolean;
  onClose: () => void;
  selectedBenefitDetail: BenefitDetail[];
  selectedBenefitIndex: number;
  handleBenefitClick: (index: number) => void;
}

const useFullScreenDialogStyles = makeStyles(_theme => ({
  swipe: {
    height: '75%',
    '& .react-swipeable-view-container': {
      height: '100%',
    },
  },
  stepper: {
    border: '1px solid #ccc',
    borderRadius: '6.25rem',
    width: '95%',
    margin: '0 auto',
    '& .MuiMobileStepper-dots': {
      padding: '0.75rem',
      borderRadius: '3.125rem',
      border: '1px solid #ccc',
    },
  },
  footer: {
    marginTop: '1rem',
    borderTop: '1px solid #ccc',
    paddingTop: '1.5rem',
  },
  nextElementInfo: {
    padding: '0.5rem 1rem',
    textAlign: 'center',
    fontSize: '15px',
  },
  dialogTitle: {
    backgroundColor: '#0033A0',
    color: '#fff',
    fontSize: '1rem',
    fontWeight: 700,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    paddingLeft: '2.5rem',
    maxHeight: '12%',
  },
  attributeList: {
    paddingLeft: '1.5rem',
    paddingRight: '1.5rem',
  },
  attributeListItem: {
    paddingTop: '1rem',
    paddingBottom: '1rem',
  },
  attributeName: {
    fontSize: '0.95rem',
  },
  attributeValue: {
    fontSize: '1rem',
  },
}));

const FullScreenDialog = (props: FullScreenDialogProps) => {
  const {
    swipe,
    stepper,
    footer,
    nextElementInfo,
    dialogTitle,
    attributeList,
    attributeListItem,
    attributeName,
    attributeValue,
  } = useFullScreenDialogStyles();

  const {selectedBenefitIndex, onClose, open, selectedBenefitDetail, handleBenefitClick} = props;

  const handleClose = () => {
    onClose();
  };
  const nextElement = {label: 'Next Benefit Detail: ', text: `${selectedBenefitDetail[selectedBenefitIndex + 1]?.benefitName}`};

  return (
    <Dialog
      fullScreen
      open={open}
      TransitionComponent={Transition}
      onClose={handleClose}
      scroll="paper"
      aria-labelledby="benefit-dialog"
    >
      <DialogTitle id="benefit-dialog" disableTypography className={dialogTitle}>
        <Typography>{selectedBenefitDetail[selectedBenefitIndex].benefitName}</Typography>
        <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
          <Close aria-label="close benefit details" id="close-dialog-icon" />
        </IconButton>
      </DialogTitle>
      <SwipeableView
        nextElementInfo={nextElement}
        onSwipe={handleBenefitClick}
        styles={{swipe, stepper, footer, nextElementInfo}}
        active={selectedBenefitIndex}
      >
        {Array.isArray(selectedBenefitDetail) &&
          selectedBenefitDetail.map(({benefitName, attributes}) => (
            <List key={toKebabCase(benefitName)} className={attributeList}>
              {Array.isArray(attributes) &&
                attributes.map(({key, value}) => (
                  <ListItem key={toKebabCase(key)} className={attributeListItem}>
                    <CardFieldV2 name={key} value={value} styles={{name: attributeName, value: attributeValue}} />
                  </ListItem>
                ))}
            </List>
          ))}
      </SwipeableView>
    </Dialog>
  );
};

export {FullScreenDialog, DesktopDialog};
